import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputSwitch } from 'primereact/inputswitch';
import { MultiSelect } from 'primereact/multiselect';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Toast } from 'primereact/toast';
import api from '../../services/api';
import { BORDER_CITIES_BY_STATE, BORDER_STATES, LANG_OPTIONS } from '../../constants/constants';
import './WaitPatternsManager.css';

const WEEK_OPTIONS = [
  { label: '1 week', value: 1 },
  { label: '2 weeks', value: 2 },
  { label: '3 weeks', value: 3 },
  { label: '4 weeks', value: 4 },
  { label: '8 weeks', value: 8 },
];

const LANE_OPTIONS = [
  { value: 'passenger:ready', label: 'Ready Lane (vehicles)' },
  { value: 'passenger:standard', label: 'Standard (vehicles)' },
  { value: 'passenger:sentri', label: 'SENTRI / NEXUS' },
  { value: 'pedestrian:ready', label: 'Ready Lane (pedestrian)' },
  { value: 'pedestrian:standard', label: 'Standard (pedestrian)' },
  { value: 'commercial:standard', label: 'Standard (commercial)' },
  { value: 'commercial:fast', label: 'FAST (commercial)' },
];

const DAY_SHORT = {
  1: 'Mon',
  2: 'Tue',
  3: 'Wed',
  4: 'Thu',
  5: 'Fri',
  6: 'Sat',
  0: 'Sun',
};

const DAY_ORDER = [1, 2, 3, 4, 5, 6, 0];

function pageLang(page) {
  return String(page?.lang || 'ES').toUpperCase();
}

function sortPages(pages) {
  return [...pages].sort((a, b) => {
    const city = String(a.departure?.city || '').localeCompare(String(b.departure?.city || ''));
    if (city) return city;
    const title = String(a.title || a.h1 || '').localeCompare(String(b.title || b.h1 || ''));
    if (title) return title;
    return String(a.full_slug || '').localeCompare(String(b.full_slug || ''));
  });
}

function pageLabel(page) {
  const title = page?.title || page?.h1 || page?.full_slug || page?._id;
  const slug = page?.full_slug ? ` · ${page.full_slug}` : '';
  return `${title}${slug}`;
}

function portsFromPage(page) {
  const out = [];
  for (const group of page?.groups || []) {
    for (const port of group.ports || []) {
      if (!port.number) continue;
      out.push({
        number: String(port.number),
        label: port.label || group.crossing_name || port.port_name || port.number,
      });
    }
  }
  return out;
}

function lerp(a, b, t) {
  return Math.round(a + (b - a) * t);
}

function heatColor(median, thin) {
  if (median == null || thin) return '#e5e7eb';
  const t = Math.max(0, Math.min(1, Number(median) / 90));
  if (t < 0.45) {
    const u = t / 0.45;
    return `rgb(${lerp(34, 250, u)}, ${lerp(197, 204, u)}, ${lerp(94, 21, u)})`;
  }
  const u = (t - 0.45) / 0.55;
  return `rgb(${lerp(250, 239, u)}, ${lerp(204, 68, u)}, ${lerp(21, 68, u)})`;
}

function heatText(median, thin) {
  if (median == null || thin) return '#6b7280';
  return Number(median) >= 50 ? '#fff' : '#111827';
}

function formatHour(hour) {
  const h = ((hour + 11) % 12) + 1;
  const suffix = hour < 12 ? 'am' : 'pm';
  return `${h} ${suffix}`;
}

function formatWindow(fromHour, toHour) {
  return `${formatHour(fromHour)}–${formatHour(toHour)}`;
}

function formatMins(mins) {
  if (mins == null) return '—';
  if (mins <= 0) return 'no wait';
  return `${mins} min`;
}

async function copyText(text) {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.left = '-9999px';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  }
}

export default function WaitPatternsManager() {
  const toast = useRef(null);
  const [pages, setPages] = useState([]);
  const [loadingPages, setLoadingPages] = useState(true);
  const [lang, setLang] = useState('ES');
  const [stateCode, setStateCode] = useState(null);
  const [cityCode, setCityCode] = useState(null);
  const [entryPageId, setEntryPageId] = useState(null);
  const [ports, setPorts] = useState([]);
  const [lane, setLane] = useState('passenger:ready');
  const [weeks, setWeeks] = useState(2);
  const [excludeOddDays, setExcludeOddDays] = useState(true);
  const [activePort, setActivePort] = useState(null);
  const [result, setResult] = useState(null);
  const [loadingResult, setLoadingResult] = useState(false);
  const requestId = useRef(0);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/wait_patterns/entry-pages');
        setPages(Array.isArray(data) ? data : []);
      } catch (err) {
        toast.current?.show({
          severity: 'error',
          summary: 'Error',
          detail: 'Could not load entry pages',
        });
      } finally {
        setLoadingPages(false);
      }
    })();
  }, []);

  const langPages = useMemo(
    () => pages.filter((p) => pageLang(p) === lang),
    [pages, lang]
  );

  const stateOptions = useMemo(() => {
    const present = new Set(
      langPages.map((p) => p.departure?.state).filter(Boolean)
    );
    return BORDER_STATES.filter((s) => present.has(s.code));
  }, [langPages]);

  const cityOptions = useMemo(() => {
    if (!stateCode) return [];
    const present = new Set(
      langPages
        .filter((p) => p.departure?.state === stateCode)
        .map((p) => p.departure?.city)
        .filter(Boolean)
    );
    const catalog = BORDER_CITIES_BY_STATE[stateCode] || [];
    const fromCatalog = catalog.filter((c) => present.has(c.code));
    const extra = [...present]
      .filter((code) => !catalog.some((c) => c.code === code))
      .map((code) => ({ code, name: code }));
    return [...fromCatalog, ...extra];
  }, [langPages, stateCode]);

  const filteredPages = useMemo(() => {
    let list = langPages;
    if (stateCode) {
      list = list.filter((p) => p.departure?.state === stateCode);
    }
    if (cityCode) {
      list = list.filter((p) => p.departure?.city === cityCode);
    }
    return sortPages(list);
  }, [langPages, stateCode, cityCode]);

  const pageOptions = useMemo(
    () =>
      filteredPages.map((p) => ({
        ...p,
        label: pageLabel(p),
      })),
    [filteredPages]
  );

  const selectedPage = useMemo(
    () => filteredPages.find((p) => String(p._id) === String(entryPageId)) || null,
    [filteredPages, entryPageId]
  );

  const portOptions = useMemo(() => {
    const list = portsFromPage(selectedPage);
    const seen = new Set();
    return list.filter((p) => {
      if (seen.has(p.number)) return false;
      seen.add(p.number);
      return true;
    });
  }, [selectedPage]);

  useEffect(() => {
    if (!entryPageId) {
      setPorts([]);
      return;
    }
    const still = filteredPages.some((p) => String(p._id) === String(entryPageId));
    if (still) return;
    setEntryPageId(null);
    setPorts([]);
    setResult(null);
  }, [filteredPages, entryPageId]);

  const loadPatterns = useCallback(async () => {
    if (!entryPageId || !selectedPage) {
      setResult(null);
      return;
    }
    if (!portOptions.length) {
      setResult(null);
      return;
    }
    const id = ++requestId.current;
    setLoadingResult(true);
    try {
      const params = {
        entry_page_id: entryPageId,
        lane,
        weeks,
        exclude_odd_days: excludeOddDays,
      };
      if (ports.length && ports.length < portOptions.length) {
        params.ports = ports.join(',');
      }
      const { data } = await api.get('/wait_patterns', { params });
      if (id !== requestId.current) return;
      setResult(data);
      const first = data?.ports?.[0]?.number;
      setActivePort((prev) => {
        if (prev && data?.ports?.some((p) => p.number === prev)) return prev;
        return first || null;
      });
    } catch (err) {
      if (id !== requestId.current) return;
      setResult(null);
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: err?.response?.data?.error || 'Could not compute wait patterns',
      });
    } finally {
      if (id === requestId.current) setLoadingResult(false);
    }
  }, [entryPageId, selectedPage, lane, weeks, excludeOddDays, ports, portOptions.length]);

  useEffect(() => {
    loadPatterns();
  }, [loadPatterns]);

  const activePortData = useMemo(
    () => result?.ports?.find((p) => p.number === activePort) || result?.ports?.[0] || null,
    [result, activePort]
  );

  const heatmapByDay = useMemo(() => {
    const map = {};
    for (const cell of activePortData?.heatmap || []) {
      if (!map[cell.weekday]) map[cell.weekday] = [];
      map[cell.weekday].push(cell);
    }
    return map;
  }, [activePortData]);

  const onCopy = async () => {
    const text = result?.briefing || '';
    if (!text) return;
    const ok = await copyText(text);
    toast.current?.show({
      severity: ok ? 'success' : 'warn',
      summary: ok ? 'Copied' : 'Could not copy',
      detail: ok ? 'Briefing copied to clipboard' : 'Copy the text manually',
    });
  };

  return (
    <div className="wp-page">
      <Toast ref={toast} />
      <header className="wp-header">
        <p className="wp-kicker">Lookup</p>
        <h2 className="wp-title">Wait patterns</h2>
      </header>

      <div className="wp-filters">
        <div className="wp-field">
          <label>Language</label>
          <Dropdown
            value={lang}
            options={LANG_OPTIONS}
            optionLabel="name"
            optionValue="code"
            onChange={(e) => setLang(e.value || 'ES')}
            className="w-full"
          />
        </div>
        <div className="wp-field">
          <label>State</label>
          <Dropdown
            value={stateCode}
            options={stateOptions}
            optionLabel="name"
            optionValue="code"
            showClear
            filter
            filterBy="name"
            placeholder="All"
            onChange={(e) => {
              setStateCode(e.value || null);
              setCityCode(null);
            }}
            className="w-full"
          />
        </div>
        <div className="wp-field">
          <label>City</label>
          <Dropdown
            value={cityCode}
            options={cityOptions}
            optionLabel="name"
            optionValue="code"
            showClear
            filter
            filterBy="name"
            placeholder="All"
            disabled={!stateCode}
            onChange={(e) => setCityCode(e.value || null)}
            className="w-full"
          />
        </div>
        <div className="wp-field">
          <label>Entry page</label>
          <Dropdown
            value={entryPageId}
            options={pageOptions}
            optionLabel="label"
            optionValue="_id"
            filter
            filterBy="label"
            showClear
            placeholder={loadingPages ? 'Loading…' : 'Select an entry page'}
            onChange={(e) => {
              const id = e.value || null;
              setEntryPageId(id);
              if (!id) {
                setPorts([]);
                setResult(null);
                return;
              }
              const page = filteredPages.find((p) => String(p._id) === String(id));
              const seen = new Set();
              setPorts(
                portsFromPage(page)
                  .map((p) => p.number)
                  .filter((n) => (seen.has(n) ? false : (seen.add(n), true)))
              );
            }}
            className="w-full"
            disabled={!pageOptions.length}
          />
        </div>
      </div>

      <div className="wp-filters-secondary">
        <div className="wp-field wp-field-ports">
          <label>Crossings</label>
          <MultiSelect
            value={ports}
            options={portOptions}
            optionLabel="label"
            optionValue="number"
            filterInputAutoFocus
            onChange={(e) => setPorts(e.value || [])}
            placeholder="All crossings on this page"
            className="w-full"
            display="chip"
            maxSelectedLabels={2}
            selectedItemsLabel="{0} crossings"
            disabled={!portOptions.length}
          />
        </div>
        <div className="wp-field">
          <label>Lane</label>
          <Dropdown
            value={lane}
            options={LANE_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => setLane(e.value)}
            className="w-full"
          />
        </div>
        <div className="wp-field">
          <label>Range</label>
          <Dropdown
            value={weeks}
            options={WEEK_OPTIONS}
            optionLabel="label"
            optionValue="value"
            onChange={(e) => setWeeks(e.value)}
            className="w-full"
          />
        </div>
        <label className="wp-switch">
          <InputSwitch
            checked={excludeOddDays}
            onChange={(e) => setExcludeOddDays(Boolean(e.value))}
          />
          <span>
            <strong>Exclude holidays / odd days</strong>
            <small>Drops Holy Week, Super Bowl, and statistical outliers</small>
          </span>
        </label>
      </div>

      {loadingPages || loadingResult ? (
        <div className="wp-loading">
          <ProgressSpinner style={{ width: 36, height: 36 }} strokeWidth="4" />
          <span>{loadingPages ? 'Loading entry pages…' : 'Computing patterns…'}</span>
        </div>
      ) : !filteredPages.length ? (
        <div className="wp-empty">No entry pages match these filters.</div>
      ) : !selectedPage ? (
        <div className="wp-empty">Select an entry page to load wait patterns.</div>
      ) : !portOptions.length ? (
        <div className="wp-empty">This entry page has no ports to analyze.</div>
      ) : !result?.ports?.length ? (
        <div className="wp-empty">No wait-time data for this page.</div>
      ) : (
        <>
          <div className="wp-highlights">
            {(result.ports.length === 1
              ? result.ports
              : result.ports.filter((p) => p.number === activePortData?.number)
            ).map((port) => (
              <div key={`${port.number}-cards`} className="contents-wrap" style={{ display: 'contents' }}>
                <article className="wp-card gold">
                  <p className="wp-card-kicker">Lowest 2-hour median</p>
                  <h3>
                    {port.week_best
                      ? `${DAY_SHORT[port.week_best.weekday]} ${formatWindow(port.week_best.fromHour, port.week_best.toHour)}`
                      : 'Insufficient samples'}
                  </h3>
                  <p>
                    {port.week_best
                      ? `${port.label} · ${formatMins(port.week_best.median)} median wait`
                      : port.label}
                  </p>
                </article>
                <article className="wp-card peak">
                  <p className="wp-card-kicker">Highest hourly median</p>
                  <h3>
                    {port.week_worst
                      ? `${DAY_SHORT[port.week_worst.weekday]} ${formatHour(port.week_worst.hour)}`
                      : 'Insufficient samples'}
                  </h3>
                  <p>
                    {port.week_worst
                      ? `${port.label} · ${formatMins(port.week_worst.median)} median wait`
                      : port.label}
                  </p>
                </article>
              </div>
            ))}
          </div>

          <section className="wp-card" style={{ marginBottom: '1rem' }}>
            <div className="wp-card-head">
              <div>
                <p className="wp-card-kicker">Day × hour heatmap</p>
                <h3 style={{ margin: '0.2rem 0 0', fontSize: '1.05rem' }}>
                  Median wait · {result.meta.lane_label}
                </h3>
                <p className="wp-meta">
                  {result.meta.timezone} · {result.meta.weeks === 1 ? '1 week' : `${result.meta.weeks} weeks`} · {result.meta.sample_count} samples
                  {result.meta.exclude_odd_days
                    ? ` · ${result.meta.excluded_dates?.length || 0} dates excluded`
                    : ''}
                </p>
              </div>
            </div>
            {result.ports.length > 1 && (
              <div className="wp-port-tabs">
                {result.ports.map((p) => (
                  <button
                    key={p.number}
                    type="button"
                    className={p.number === activePortData?.number ? 'is-active' : ''}
                    onClick={() => setActivePort(p.number)}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            )}
            <div className="wp-heatmap-wrap">
              <table className="wp-heatmap">
                <thead>
                  <tr>
                    <th />
                    {Array.from({ length: 24 }, (_, hour) => (
                      <th key={hour}>{hour % 2 === 0 ? hour : ''}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DAY_ORDER.map((weekday) => (
                    <tr key={weekday}>
                      <td className="wp-day">{DAY_SHORT[weekday]}</td>
                      {(heatmapByDay[weekday] || []).map((cell) => (
                        <td key={`${weekday}-${cell.hour}`}>
                          <div
                            className="wp-cell"
                            title={`${DAY_SHORT[weekday]} ${formatHour(cell.hour)} · ${
                              cell.median == null ? 'no data' : `${cell.median} min`
                            } · n=${cell.n}${cell.thin ? ' (thin sample)' : ''}`}
                            style={{
                              background: heatColor(cell.median, cell.thin),
                              color: heatText(cell.median, cell.thin),
                            }}
                          >
                            {cell.median == null ? '' : cell.median}
                          </div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="wp-legend">
              <span>Faster</span>
              <div className="wp-legend-bar" />
              <span>Slower</span>
              <span>· gray = thin sample</span>
            </div>
          </section>

          <section className="wp-card" style={{ marginBottom: '1rem' }}>
            <p className="wp-card-kicker">By weekday</p>
            <h3 style={{ margin: '0.2rem 0 0.7rem', fontSize: '1.05rem' }}>
              {activePortData?.label || 'Crossing'}
            </h3>
            <table className="wp-table">
              <thead>
                <tr>
                  <th>Day</th>
                  <th>Lowest 2-hour median</th>
                  <th>Highest hourly median</th>
                  <th>1-hour shift savings</th>
                </tr>
              </thead>
              <tbody>
                {(activePortData?.days || []).map((day) => (
                  <tr key={day.weekday}>
                    <td style={{ textTransform: 'capitalize' }}>{day.name}</td>
                    <td>
                      {day.gold
                        ? `${formatWindow(day.gold.fromHour, day.gold.toHour)} · ${formatMins(day.gold.median)}`
                        : '—'}
                    </td>
                    <td>
                      {day.hell
                        ? `${formatHour(day.hell.hour)} · ${formatMins(day.hell.median)}`
                        : '—'}
                    </td>
                    <td>
                      {day.save_if_shift_1h != null
                        ? `~${formatMins(day.save_if_shift_1h)}`
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="wp-card">
            <div className="wp-card-head">
              <div>
                <p className="wp-card-kicker">Internal briefing</p>
                <h3 style={{ margin: '0.2rem 0 0', fontSize: '1.05rem' }}>Summary</h3>
              </div>
              <Button
                type="button"
                label="Copy"
                icon="pi pi-copy"
                className="p-button-sm"
                onClick={onCopy}
                disabled={!result.briefing}
              />
            </div>
            <pre className="wp-briefing">{result.briefing}</pre>
            {result.meta.excluded_dates?.length > 0 && (
              <details className="wp-excluded">
                <summary>
                  {result.meta.excluded_dates.length} excluded dates
                </summary>
                <ul>
                  {result.meta.excluded_dates.map((d) => (
                    <li key={`${d.date}-${d.reason}`}>
                      {d.date} · {d.label}
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </section>
        </>
      )}
    </div>
  );
}
